
import { Component, Vue, Watch } from 'vue-property-decorator';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { getModule } from 'vuex-module-decorators';
import MetricCard from '@/components/purchase-order/dashboard/MetricCard.vue';
import POStatusCard from '@/components/purchase-order/dashboard/POStatusCard.vue';
import QuickActionsBar from '@/components/purchase-order/dashboard/QuickActionsBar.vue';
import ActivityFeed from '@/components/purchase-order/dashboard/ActivityFeed.vue';
import PODashboardModule from '@/store/modules/poDashboard';
import { formatDistanceToNow } from 'date-fns';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import { getFeatureVersion, setFeatureVersion, purchaseOrderSwitchTarget } from '@/config/atlasFeatures';

@Component({
  components: {
    MetricCard,
    POStatusCard,
    QuickActionsBar,
    ActivityFeed,
    CopyLinkButton,
    TryNewAtlasBanner
  }
})
export default class PODashboard extends Vue {
  private poDashboardStore = getModule(PODashboardModule, this.$store);

  get showTryNewPoBanner(): boolean {
    return getFeatureVersion('purchaseOrder') === 'classic';
  }

  get metrics() {
    return this.poDashboardStore.metrics || {
      openPOs: { count: 0, totalValue: 0, trend: 0 },
      pendingApprovals: 0,
      upcomingDeliveries: { count: 0, suppliers: [] },
      activeAutomations: 0,
      nextAutomationRun: null,
      statusCounts: {
        draft: 0, confirmed: 0,
        partial_received: 0, received: 0, completed: 0, canceled: 0
      },
      statusTrends: {
        draft: 0, confirmed: 0,
        partial_received: 0, received: 0, completed: 0, canceled: 0
      },
      lowStockAlerts: 0,
      payment: { awaitingPayment: 0, overdue: 0 }
    };
  }

  get activities() {
    return this.poDashboardStore.activities;
  }

  get loading() {
    return this.poDashboardStore.loading;
  }

  get error() {
    return this.poDashboardStore.error;
  }

  get lastUpdated() {
    return this.poDashboardStore.lastUpdated;
  }

  initialLoading = true;
  activityFilter = 'all';
  
  activityFilterOptions = [
    { text: 'All Activities', value: 'all' },
    { text: 'PO Created', value: 'created' },
    { text: 'PO Received', value: 'received' },
    { text: 'Automation', value: 'automation' },
    { text: 'Approvals', value: 'approval' }
  ];

  get keyMetrics() {
    return [
      {
        key: 'openPOs',
        title: 'Open POs',
        value: this.metrics?.openPOs?.count || 0,
        subtitle: this.formatCurrency(this.metrics?.openPOs?.totalValue || 0),
        icon: 'mdi-file-document-outline',
        color: '#59C8E4',
        trend: this.metrics?.openPOs?.trend,
        action: () => this.navigateToList('open')
      },
      {
        key: 'pendingApprovals',
        title: 'Pending Approvals',
        value: this.metrics?.pendingApprovals || 0,
        subtitle: (this.metrics?.pendingApprovals || 0) > 0 ? 'Action required' : 'All clear',
        icon: 'mdi-clock-alert-outline',
        color: (this.metrics?.pendingApprovals || 0) > 0 ? '#faaf3b' : '#99be50',
        trend: null,
        action: () => this.$router.push('/purchase-orders/unapproved')
      },
      {
        key: 'upcomingDeliveries',
        title: 'Upcoming Deliveries',
        value: this.metrics?.upcomingDeliveries?.count || 0,
        subtitle: 'Next 7 days',
        icon: 'mdi-truck-delivery',
        color: '#59C8E4',
        trend: null,
        action: () => this.navigateToList('expecting')
      },
      {
        key: 'automationConfigs',
        title: 'Active Automations',
        value: this.metrics?.activeAutomations || 0,
        subtitle: 'Automation configurations',
        icon: 'mdi-robot',
        color: '#2c4051',
        trend: null,
        action: () => this.$router.push('/automation-configs')
      },
      {
        key: 'overduePayment',
        title: 'Overdue Payments',
        value: this.metrics?.payment?.overdue || 0,
        subtitle: `${this.metrics?.payment?.awaitingPayment || 0} awaiting payment`,
        icon: 'mdi-cash-clock',
        color: (this.metrics?.payment?.overdue || 0) > 0 ? '#e53935' : '#99be50',
        trend: null,
        action: () => this.navigateToList('overdue')
      }
    ];
  }

  get statusCards() {
    return [
      {
        key: 'draft',
        label: 'Draft',
        count: this.metrics?.statusCounts?.draft || 0,
        color: '#99be50',
        icon: 'mdi-file-edit-outline',
        trend: this.metrics?.statusTrends?.draft
      },
      {
        key: 'confirmed',
        label: 'Confirmed',
        count: this.metrics?.statusCounts?.confirmed || 0,
        color: '#99be50',
        icon: 'mdi-check-circle-outline',
        trend: this.metrics?.statusTrends?.confirmed
      },
      {
        key: 'partial_received',
        label: 'Partial',
        count: this.metrics?.statusCounts?.partial_received || 0,
        color: '#faaf3b',
        icon: 'mdi-package-variant',
        trend: this.metrics?.statusTrends?.partial_received
      },
      {
        key: 'received',
        label: 'Received',
        count: this.metrics?.statusCounts?.received || 0,
        color: '#99be50',
        icon: 'mdi-package-check',
        trend: this.metrics?.statusTrends?.received
      },
      {
        key: 'completed',
        label: 'Completed',
        count: this.metrics?.statusCounts?.completed || 0,
        color: '#00796b',
        icon: 'mdi-check-circle',
        trend: this.metrics?.statusTrends?.completed
      },
      {
        key: 'canceled',
        label: 'Canceled',
        count: this.metrics?.statusCounts?.canceled || 0,
        color: '#f6687b',
        icon: 'mdi-close-circle-outline',
        trend: this.metrics?.statusTrends?.canceled
      }
    ];
  }

  get filteredActivities() {
    if (this.activityFilter === 'all') {
      return this.activities;
    }
    return this.activities.filter(activity => activity.type === this.activityFilter);
  }

  get hasError() {
    return this.error !== null;
  }

  get errorMessage() {
    return this.error || 'Failed to load dashboard data';
  }

  get formatLastUpdated() {
    if (!this.lastUpdated) return '';
    return formatDistanceToNow(this.lastUpdated, { addSuffix: true });
  }

  async mounted() {
    await this.loadDashboard();
  }

  async loadDashboard() {
    this.initialLoading = true;
    try {
      await this.poDashboardStore.fetchDashboardData();
    } finally {
      this.initialLoading = false;
    }
  }

  async refreshDashboard() {
    await this.poDashboardStore.fetchDashboardData();
  }

  navigateToList(key: string) {
    // The Overdue Payments card filters by payment state, not fulfilment status.
    const query = key === 'overdue' ? { overdue: 'true' } : { status: key };
    this.$router.push({
      path: '/purchase-orders',
      query
    });
  }

  navigateToPO(poId: number) {
    this.$router.push(`/purchase-orders/${poId}`);
  }

  handleQuickAction(action: string) {
    switch (action) {
      case 'create':
        this.$router.push('/purchase-orders/create');
        break;
      case 'create-from-buying-table':
        this.$router.push('/buying-table');
        break;
      case 'create-for-supplier':
        this.$router.push('/suppliers');
        break;
      // Import action hidden
      // case 'import':
      //   this.$router.push('/purchase-orders/create');
      //   break;
      case 'approvals':
        this.$router.push('/purchase-orders/unapproved');
        break;
      case 'automation':
        this.$router.push('/automation-configs');
        break;
      case 'view-all':
        this.$router.push('/purchase-orders');
        break;
    }
  }

  filterActivity() {
    // Activity filtering is handled by computed property
  }

  async loadMoreActivities() {
    await this.poDashboardStore.loadMoreActivities();
  }

  formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrencyWithCode(value, CurrencyHelper.getStoreCurrency());
  }

  switchPoToAtlas(): void {
    setFeatureVersion('purchaseOrder', 'atlas');
    this.$router.push(purchaseOrderSwitchTarget('poDashboard'));
  }

  // formatNextRun method removed - next run time is now hidden
}
